import { useState } from "react";

import { ChevronDown, ChevronRight, Lock, Settings } from "lucide-react";
import { NavLink } from "react-router";

import { NAV_DASHBOARD, visibleNavSections } from "@/app/routes/navigation";
import type { NavItem } from "@/app/routes/navigation";
import { Button } from "@/shared/components/ui/button";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { toast } from "@/shared/components/ui/toast";
import { cn } from "@/shared/components/ui/utils";
import { useAuth } from "@/shared/contexts/AuthContext";

function SidebarLink({ item, indented = false }: { item: NavItem; indented?: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        cn(
          "flex w-full items-center gap-3 rounded-md px-4 py-2 text-slate-300 transition-colors hover:bg-slate-700/50 hover:text-white",
          indented && "text-sm",
          isActive && "bg-slate-700 text-white",
        )
      }
      end
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}

/** A section that is one destination — no chevron, no children. */
function SidebarSectionLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        cn(
          "flex w-full items-center gap-3 rounded-md px-4 py-2 text-slate-300 transition-colors hover:bg-slate-700/50 hover:text-white",
          isActive && "bg-slate-700 text-white",
        )
      }
      /* Tabbed screens live under this path, and the row stays selected
         while the operator moves between their tabs. */
      end={!item.matchNested}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}

/**
 * Primary application navigation. Rendered entirely from the navigation
 * config — adding a module to NAV_SECTIONS is all that is needed to
 * surface it here and in the header title resolution.
 */
export function Sidebar() {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const { hasPermission } = useAuth();

  /* Recomputed per render because a silent refresh can widen or narrow the
     grants mid-session; the list is six entries, so memoising costs more
     than it saves. */
  const sections = visibleNavSections(hasPermission);

  const toggleSection = (id: string) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <nav
      aria-label="Main navigation"
      className="fixed left-0 top-0 flex h-screen w-[240px] flex-col bg-app-sidebar text-white"
    >
      {/* Brand */}
      <div className="flex flex-shrink-0 items-start gap-3 border-b border-slate-700 px-4 py-4">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-600"
          aria-hidden="true"
        >
          {/* Railway wheelset — outer rim + hub + 6 spokes. Matches favicon.svg. */}
          <svg viewBox="0 0 64 64" className="h-6 w-6" role="img" aria-label="WMS logo">
            <circle cx="32" cy="32" r="22" fill="none" stroke="currentColor" strokeWidth="4" />
            <circle cx="32" cy="32" r="5" fill="currentColor" />
            <g stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="32" y1="12" x2="32" y2="52" />
              <line x1="14.68" y1="22" x2="49.32" y2="42" />
              <line x1="14.68" y1="42" x2="49.32" y2="22" />
            </g>
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight text-white">WMS</p>
          <p className="mt-0.5 text-[11px] leading-tight text-slate-400">
            Wagon Repair Workshop,
            <br />
            Vadlapudi · South Coast Railway
          </p>
        </div>
      </div>

      <div className="flex-shrink-0 px-6 pb-3 pt-6">
        <p className="text-xs uppercase tracking-wider text-slate-400">Main Menu</p>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="px-3 pb-4">
          <div className="mb-2">
            <SidebarLink item={NAV_DASHBOARD} />
          </div>

          {sections.map((section) => {
            const isOpen = !collapsed[section.id];
            const SectionIcon = section.icon;

            if (section.link) {
              return (
                <div key={section.id} className="mb-2">
                  <SidebarSectionLink item={section.link} />
                </div>
              );
            }

            if (section.comingSoon) {
              return (
                <div key={section.id} className="mb-2">
                  <Button
                    variant="ghost"
                    disabled
                    className="w-full cursor-not-allowed justify-start gap-2 text-slate-400 opacity-60 hover:bg-slate-700/50 hover:text-slate-300"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    {SectionIcon && <SectionIcon className="h-4 w-4" aria-hidden="true" />}
                    <span className="flex-1 text-left">{section.label}</span>
                    <span className="rounded bg-slate-600 px-2 py-0.5 text-xs">Soon</span>
                  </Button>
                </div>
              );
            }

            if (section.locked) {
              return (
                <div key={section.id} className="mb-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-slate-300 hover:bg-slate-700/50 hover:text-white"
                    onClick={() => toast.error(`You don't have access to ${section.label}.`)}
                  >
                    <Lock className="h-4 w-4" aria-hidden="true" />
                    {SectionIcon && <SectionIcon className="h-4 w-4" aria-hidden="true" />}
                    <span className="flex-1 text-left">{section.label}</span>
                  </Button>
                </div>
              );
            }

            return (
              <div key={section.id} className="mb-2">
                <Button
                  variant="ghost"
                  aria-expanded={isOpen}
                  className="mb-1 w-full justify-start gap-2 text-slate-300 hover:bg-slate-700/50 hover:text-white"
                  onClick={() => toggleSection(section.id)}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span>{section.label}</span>
                </Button>

                {isOpen && (
                  <div className="ml-6 mt-1 space-y-1">
                    {section.items.map((item) => (
                      <SidebarLink key={item.path} item={item} indented />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Settings at bottom */}
      <div className="flex-shrink-0 border-t border-slate-700 p-3">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-slate-300 hover:bg-slate-700/50 hover:text-white"
        >
          <Settings className="h-5 w-5" aria-hidden="true" />
          <span>Settings</span>
        </Button>
      </div>
    </nav>
  );
}
